import { useContext } from "react";
import { Button, Input, Modal, Select, Table, Tag, Typography } from "antd";
import {
    NotificationOutlined,
    PlusOutlined,
    SearchOutlined,
} from "@ant-design/icons";

import useNotice from "./useNotice";
import PageHeader from "../../components/PageHeader";
import ActiveFiltersBar from "../../components/ActiveFiltersBar";
import TableCard from "../../components/TableCard";
import TableDicaHint from "../../components/TableDicaHint";
import TablePagination from "../../components/TablePagination";
import { ThemeContext } from "../../contexts/ThemeContext";
import { poeValorComPontosEmMil } from "../../utils/Conversores";

const { Paragraph } = Typography;

export default function Notices() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const {
        isLoading,
        isLoadingDelete,
        isLoadingUpload,
        isDeleteModalVisible,
        noticeBeingDeleted,
        isUpdateNoticeModalVisible,
        noticeBeingUpdated,
        columns,
        tableData,
        filteredData,
        searchText,
        setSearchText,
        filterStatus,
        setFilterStatus,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        activeFilterItems,
        handleClickCreateNewNotice,
        handleClickEditNotice,
        handleConfirmDeleteNotice,
        handleCancelDeleteNotice,
        handleConfirmUpdateNotice,
        handleCancelUpdateNotice,
        handleClearAllFilters,
    } = useNotice();

    const isUpdatingActive =
        typeof noticeBeingUpdated?.status === "string"
            ? noticeBeingUpdated?.status === "true"
            : Boolean(noticeBeingUpdated?.status);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Page Header */}
            <PageHeader
                icon={<NotificationOutlined />}
                iconBg="linear-gradient(135deg, #fa8c16 0%, #fa541c 100%)"
                title="Avisos e Notícias"
                tag={<Tag color="orange">{poeValorComPontosEmMil(tableData.length)} cadastrados</Tag>}
                subtitle="Gerencie comunicados e notícias exibidos na tela inicial do aplicativo para os cidadãos"
                actions={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleClickCreateNewNotice}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 600,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Novo Aviso
                    </Button>
                }
            />

            {/* Filter Bar / Controls */}
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                    borderRadius: 10,
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    gap: 14,
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 240, maxWidth: 380 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                        Pesquisar Aviso
                    </span>
                    <Input
                        placeholder="Buscar por título do aviso..."
                        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            setCurrentPage(1);
                        }}
                        allowClear
                        style={{ borderRadius: 8, height: 36 }}
                    />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4, width: 180 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                        Status do Aviso
                    </span>
                    <Select
                        value={filterStatus}
                        onChange={(val) => {
                            setFilterStatus(val);
                            setCurrentPage(1);
                        }}
                        style={{ width: "100%", height: 36 }}
                        options={[
                            { value: "ALL", label: "Todos os Status" },
                            { value: "ACTIVE", label: "Ativos" },
                            { value: "INACTIVE", label: "Inativos" },
                        ]}
                    />
                </div>
            </div>

            {/* Active Filters Bar */}
            <ActiveFiltersBar
                filters={activeFilterItems}
                onClearAll={handleClearAllFilters}
            />

            {/* Hint & Pagination (Top) */}
            <TableDicaHint text="Clique em uma linha para editar o aviso ou use as ações para ativar, inativar ou excluir." />

            <TablePagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                itemName="avisos"
                pageSizeOptions={["10", "25", "50", "100"]}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                }}
            />

            {/* Table Card */}
            <TableCard>
                <Table
                    columns={columns as any}
                    dataSource={filteredData.slice(
                        (currentPage - 1) * pageSize,
                        currentPage * pageSize
                    )}
                    rowKey="key"
                    loading={isLoading}
                    pagination={false}
                    className={`modern-data-table ${isDark ? "modern-data-table-dark" : ""}`}
                    scroll={{ x: 650 }}
                    onRow={(record) => ({
                        onClick: () => handleClickEditNotice(record),
                        style: { cursor: "pointer" },
                    })}
                />
            </TableCard>

            {/* Pagination (Bottom) */}
            <TablePagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                itemName="avisos"
                pageSizeOptions={["10", "25", "50", "100"]}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                }}
            />

            {/* Delete Confirmation Modal */}
            <Modal
                open={isDeleteModalVisible}
                onOk={handleConfirmDeleteNotice}
                onCancel={handleCancelDeleteNotice}
                confirmLoading={isLoadingDelete}
                okText="Sim, Deletar"
                cancelText="Cancelar"
                okType="danger"
                okButtonProps={{ loading: isLoadingDelete }}
                cancelButtonProps={{ disabled: isLoadingDelete }}
                centered
                title="Confirmar Exclusão de Aviso"
            >
                <Paragraph style={{ marginTop: 12 }}>
                    Deseja realmente deletar o aviso{" "}
                    <strong>"{noticeBeingDeleted?.title}"</strong>?
                </Paragraph>
                <div
                    style={{
                        background: isDark ? "#1f1315" : "#fff1f0",
                        border: `1px solid ${isDark ? "#431418" : "#ffa39e"}`,
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontSize: 12.5,
                        color: isDark ? "#ffa39e" : "#cf1322",
                    }}
                >
                    Esta ação removerá este comunicado do painel e ele não estará mais visível aos cidadãos no aplicativo.
                </div>
            </Modal>

            {/* Status Update Confirmation Modal */}
            <Modal
                open={isUpdateNoticeModalVisible}
                onOk={handleConfirmUpdateNotice}
                onCancel={handleCancelUpdateNotice}
                confirmLoading={isLoadingUpload}
                okText={isUpdatingActive ? "Sim, Inativar" : "Sim, Ativar"}
                cancelText="Cancelar"
                okType={isUpdatingActive ? "danger" : "primary"}
                okButtonProps={{
                    loading: isLoadingUpload,
                    danger: isUpdatingActive,
                }}
                cancelButtonProps={{ disabled: isLoadingUpload }}
                centered
                title={
                    isUpdatingActive
                        ? "Confirmar Inativação de Aviso"
                        : "Confirmar Ativação de Aviso"
                }
            >
                <Paragraph style={{ marginTop: 12 }}>
                    Deseja realmente {isUpdatingActive ? "inativar" : "ativar"} o aviso{" "}
                    <strong>"{noticeBeingUpdated?.title}"</strong>?
                </Paragraph>
                <div
                    style={{
                        background: isDark ? "#141414" : isUpdatingActive ? "#fff1f0" : "#f6ffed",
                        border: `1px solid ${
                            isDark
                                ? "#262626"
                                : isUpdatingActive
                                ? "#ffa39e"
                                : "#b7eb8f"
                        }`,
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontSize: 12.5,
                        color: isDark
                            ? "#e2e8f0"
                            : isUpdatingActive
                            ? "#cf1322"
                            : "#389e0d",
                    }}
                >
                    {isUpdatingActive
                        ? "Ao inativar este aviso, ele deixará de ser exibido na tela inicial do aplicativo para os cidadãos."
                        : "Ao ativar este aviso, ele voltará a ser exibido na tela inicial do aplicativo para os cidadãos."}
                </div>
            </Modal>
        </div>
    );
}
